import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Coins, Play, Eye, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import type { RootState } from '@/state/store';
import {
  fxRevaluationApi, type FxRevaluationResult,
} from '@/api/fxRevaluation.api';

const inputCls =
  'w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-signal';

const money = (n: number, ccy?: string) =>
  `${(ccy ? ccy + ' ' : '')}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const FxRevaluationPage = () => {
  const { hasPermission } = usePermission();
  const canRun = hasPermission(Permissions.GL_PERIOD_CLOSE);

  // Start on the period the user is working in. These were the literals 'FY2026' and '2026-07', so
  // the screen opened on a period that was right for one company in one month and wrong for
  // everyone after: next January it would have offered to revalue a year already closed.
  const sessionFy     = useSelector((s: RootState) => s.session.session?.financialYearCode   ?? '');
  const sessionPeriod = useSelector((s: RootState) => s.session.session?.financialPeriodCode ?? '');
  const [fyCode, setFyCode] = useState(sessionFy);
  const [periodCode, setPeriodCode] = useState(sessionPeriod);

  // The session can arrive after this screen mounts (a page reload restores it asynchronously).
  // Fill the fields when it does, but never overwrite something the user has already typed.
  useEffect(() => { setFyCode(v => v || sessionFy); }, [sessionFy]);
  useEffect(() => { setPeriodCode(v => v || sessionPeriod); }, [sessionPeriod]);
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null);
  const [result, setResult] = useState<FxRevaluationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const call = async (mode: 'preview' | 'run') => {
    if (mode === 'run' && !confirm(
      `Post the FX revaluation for ${periodCode}? This books the unrealized gain/loss and its next-period reversal.`)) return;
    setBusy(mode);
    setError(null);
    try {
      const req = { financialYearCode: fyCode.trim(), periodCode: periodCode.trim() };
      const res = mode === 'preview' ? await fxRevaluationApi.preview(req) : await fxRevaluationApi.run(req);
      setResult(res);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Request failed.');
      setResult(null);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <Coins size={20} className="text-signal" /> FX Revaluation
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Revalues open foreign-currency payables and receivables at the period-end closing rate.
          The unrealized gain or loss is posted against the control accounts and reversed
          automatically on the first day of the next period; open items keep their original rate.
          Preview shows what would happen — nothing is posted until you run it.
        </p>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Controls */}
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Financial Year</label>
              <input className={inputCls} value={fyCode} onChange={e => setFyCode(e.target.value)} placeholder="FY2026" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-1">Period</label>
              <input className={inputCls} value={periodCode} onChange={e => setPeriodCode(e.target.value)} placeholder="2026-07" />
            </div>
            <button
              onClick={() => call('preview')}
              disabled={busy !== null || !fyCode.trim() || !periodCode.trim()}
              className="px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50">
              <Eye size={15} /> Preview
            </button>
            {canRun && (
              <button
                onClick={() => call('run')}
                disabled={busy !== null || !fyCode.trim() || !periodCode.trim()}
                className="px-3 py-2 text-sm font-semibold text-white bg-signal hover:bg-signal-light rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50">
                <Play size={15} /> Run &amp; Post
              </button>
            )}
            {busy && <Spinner size="sm" />}
          </div>
        </section>

        {error && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {result && (
          <>
            {result.executed && (
              <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <span>
                  Posted revaluation <strong>{result.revaluationJournalNo}</strong>
                  {result.reversalJournalNo && <> and reversal <strong>{result.reversalJournalNo}</strong> (dated {result.reversalDate})</>}.
                </span>
              </div>
            )}

            {/* Summary cards */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1"><TrendingUp size={13} className="text-emerald-500" /> Unrealized Gain</div>
                <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400 mt-1">{money(result.totalUnrealizedGain, result.baseCurrency)}</div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1"><TrendingDown size={13} className="text-red-500" /> Unrealized Loss</div>
                <div className="text-lg font-semibold text-red-600 dark:text-red-400 mt-1">{money(result.totalUnrealizedLoss, result.baseCurrency)}</div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Net Adjustment</div>
                <div className="text-lg font-semibold text-slate-900 dark:text-white mt-1">{money(result.netAdjustment, result.baseCurrency)}</div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                <div className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Period End / Reversal</div>
                <div className="text-sm font-medium text-slate-900 dark:text-white mt-1">{result.periodEndDate}</div>
                <div className="text-xs text-slate-500 dark:text-gray-400">reverses {result.reversalDate ?? '—'}</div>
              </div>
            </section>

            {result.warnings.length > 0 && (
              <div className="px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300">
                <div className="font-semibold flex items-center gap-1.5 mb-1"><AlertTriangle size={14} /> {result.warnings.length} warning(s)</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Items */}
            <section>
              <h2 className="text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Open foreign-currency balances ({result.items.length})
              </h2>
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                {result.items.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-slate-400 text-center">
                    No open foreign-currency balances to revalue for this period.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Party</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Document</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Outstanding</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Booked Base</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Rate</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Revalued</th>
                          <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase">Delta</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {result.items.map((it, i) => (
                          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                            <td className="px-4 py-2.5 text-slate-900 dark:text-white">
                              <span className="font-mono text-xs text-slate-500 dark:text-gray-400">{it.partyType}</span> {it.partyCode}
                            </td>
                            <td className="px-4 py-2.5"><span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-medium">{it.documentType}</span></td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-600 dark:text-slate-300">{money(it.outstandingForeign, it.currency)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-600 dark:text-slate-300">{money(it.bookedBase)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-600 dark:text-slate-300">{it.periodEndRate.toLocaleString(undefined, { maximumFractionDigits: 6 })}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-600 dark:text-slate-300">{money(it.revaluedBase)}</td>
                            <td className={`px-4 py-2.5 text-right font-mono text-xs font-semibold ${it.delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : it.delta < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-gray-400'}`}>
                              {it.delta > 0 ? '+' : ''}{money(it.delta)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};
